import React from 'react';
import { Shield, Users, FileText, MapPin, ThumbsUp, Share, Bell, Camera } from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout';

const LinkedInProfileHacks = () => {
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <h1 className="text-3xl font-bold mb-8">How to Hack Your LinkedIn Profile for Job Searching</h1>

        {/* Stats Section */}
        <div className="bg-blue-50 p-6 rounded-lg mb-8">
          <h2 className="font-semibold mb-4 text-lg">Key Statistics 📊</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">87%</p>
              <p>Companies use LinkedIn to find candidates</p>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">180M</p>
              <p>Senior level influencers</p>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">63M</p>
              <p>Decision Makers</p>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">10M</p>
              <p>C-Level Executives</p>
            </div>
            <div className="bg-white p-4 rounded-lg col-span-2">
              <p className="text-2xl font-bold text-blue-600">&gt;90%</p>
              <p>Recruiters use LinkedIn to source candidates</p>
            </div>
          </div>
        </div>

        {/* Privacy Section */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold">Privacy Settings 🔒</h2>
          </div>
          <div className="bg-yellow-50 p-6 rounded-lg">
            <p className="font-medium mb-2">TURN OFF "Sharing Profile Edits"</p>
            <p>Don't make the mistake of alerting your boss and/or annoying all your contacts every time you update your profile.</p>
          </div>
        </div>

        {/* Profile Sections */}
        <div className="space-y-8">
          {/* Headline and Picture */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <Camera className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold">Headline and Picture 📸</h2>
            </div>
            <p className="mb-4">Take advantage of this space. You get 120 characters on your headline so max out the space using descriptive keywords to grab attention and be found by recruiters.</p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-medium text-gray-700">Example:</p>
              <p className="text-gray-600">I Help Senior Leaders Hire The Best Director to SVP Talent Using My "Right FIT" System | Career Transition Services</p>
            </div>
          </div>

          {/* About Field */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold">About Field 📝</h2>
            </div>
            <div className="space-y-4">
              <p className="font-bold">YOU GET 2000 CHARACTERS - DON'T WASTE THE SPACE.</p>
              <p className="bg-yellow-50 p-3 rounded">FIRST 92 CHARACTERS are seen on a mobile device. Make them count!</p>
              <div className="space-y-2">
                <p className="font-medium">FOLLOW THESE TIPS:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Include your cell phone number if looking for new positions</li>
                  <li>Make it easy for recruiters to reach you</li>
                  <li>Write about your expertise with confidence</li>
                  <li>Use a variety of functional keywords</li>
                  <li>Use bullets to separate content for readability</li>
                  <li>Avoid giant paragraphs</li>
                  <li className="font-bold">DO NOT REGURGITATE YOUR RESUME</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold">Location Settings 📍</h2>
            </div>
            <p>Staying local? No worries. However, if you can relocate, then change your zip code to the United States by typing in "00000".</p>
          </div>

          {/* Social Proof */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <ThumbsUp className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold">Social Proof - Numbers Talk 📊</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p>Get 4+ Recommendations</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p>Get 18+ Endorsements</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p>Give 4+ Recommendations</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p>Give 18+ Endorsements</p>
              </div>
            </div>
          </div>

          {/* Posting & Groups */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <Share className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold">Posting & Groups 📱</h2>
            </div>
            <p className="mb-4">Posting once a week is critically important to keep you in front of your network. Sharing content is the easiest such as your network's posts but also blog posts, company or industry news, or quick tips.</p>
            <p>Groups help you build your network with professionals in your industry. You can join 50 groups so get started.</p>
          </div>

          {/* Job Seeking Settings */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <Bell className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold">If Unemployed 🔍</h2>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="font-medium mb-2">Turn on "Job Seeking Preferences"</p>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Go to your main profile page</li>
                <li>Click the down arrow next to "Me" in horizontal bar</li>
                <li>Click Settings & Privacy</li>
                <li>Scroll to "Job Seeking Preferences"</li>
                <li>Turn on "Let Recruiters Know You're Open to Opportunities"</li>
                <li>Save changes</li>
                <li>Click back to LinkedIn to return to home page</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Final Tip */}
        <div className="mt-8 bg-green-50 p-6 rounded-lg">
          <div className="flex items-center gap-2">
            <Camera className="w-6 h-6 text-green-600" />
            <p className="font-bold">Profile Picture Impact:</p>
          </div>
          <p className="mt-2">Pictures give you 21x more views and 36x more messages!</p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default LinkedInProfileHacks;